/**
 * How well a company answered a semantic search.
 *
 * # Why this is shared rather than copied
 *
 * Two lists show it — the public row and the members' row — and the bands are a
 * judgement about what counts as a good match, not styling. Copied, they drift:
 * one list calls 0.66 green and the other calls it amber, for the same company,
 * on the same search. The customer sees two answers.
 */

/**
 * The score as a percentage, rounded.
 *
 * A quality of exactly 0 is a real answer — "nothing in common" — but the
 * corpus does not produce it; what does appear is `unscored`, below.
 */
export function scorePercent(quality: number | undefined): number {
  return quality ? Math.round(quality * 100) : 0;
}

/**
 * Whether a row was ranked at all.
 *
 * The API returns -1 for a company no ranking pass scored, deliberately, so
 * that "not ranked" can be told from "ranked badly" — see `unscored` in
 * internal/httpapi/preview.go. A filtered selection is entirely unranked, and a
 * list of "-100%" badges would be worse than no badge.
 */
export function isRanked(quality: number | undefined): boolean {
  return typeof quality === 'number' && quality >= 0;
}

/**
 * The pill's colours for a score.
 *
 * Five bands, from the public row, which had them first. The thresholds are
 * where they are because a cosine similarity over these embeddings clusters
 * high: 0.8 is a close match rather than a perfect one, and anything under 0.3
 * is usually a company that shares a word rather than a business.
 */
export function scoreBand(quality: number | undefined): string {
  const value = quality ?? 0;

  if (value >= 0.8) return 'text-emerald-700 bg-emerald-50 ring-1 ring-emerald-600/20';
  if (value >= 0.65) return 'text-green-700 bg-green-50 ring-1 ring-green-600/20';
  if (value >= 0.45) return 'text-amber-700 bg-amber-50 ring-1 ring-amber-600/20';
  if (value >= 0.3) return 'text-orange-700 bg-orange-50 ring-1 ring-orange-600/20';
  return 'text-red-700 bg-red-50 ring-1 ring-red-600/20';
}
